import mapActions from "actions/map";
import actionTypes from "constants/profile";
import {
    featureModuleTypesEnum,
    organizationDemoPeriodStatusesEnum,
    organizationFlags,
    organizationFlagsEnum,
} from "helpers/enums";
import { flagVal } from "helpers/system";
import { Action } from "redux";
import { ActionFunctionAny, createActions } from "redux-actions";
import { item as getTimezoneInfo } from "sources/books/timezones";
import {
    showBidCarsPlanningSettings,
    showChatsSettings,
    showControlPointsSettings,
    showDefaultRouteOptions,
    showDriverEducationResourceSettings,
    showExploitationSettings,
    showFuelLimitOption,
    showOrg,
    showProfitabilityOfBidSettings,
    showRefuelingSettings,
    showReportExecutionTimes,
    showSyncSystems,
    showWorkRestInfo,
} from "sources/profile/organization";
import { showNotificationOptions, showUser } from "sources/profile/user";
import { create } from "sources/session";

const {
    GET_INITIAL_PROFILE,
    GET_USER,
    GET_ORG,
    GET_WORK_REST_INFO,
    GET_NOTIFICATION_OPTIONS,
    GET_TIMEZONE,
    GET_REGION_SETTINGS,
    GET_REPORT_EXECUTION_TIMES,
    GET_DEFAULT_ROUTE_OPTIONS,
    GET_CONTROL_POINTS_SETTINGS,
    GET_SYNC_SYSTEMS,
    GET_PROFITABILITY_OF_BID_SETTINGS,
    GET_DRIVER_EDUCATION_RESOURCE_SETTINGS,
    GET_CHATS_SETTINGS,
    GET_REFUELING_SETTINGS,
    GET_EXPLOITATION_SETTINGS,
    GET_BID_CARS_PLANNING_SETTINGS,
} = actionTypes;

const actions = createActions<Object>(
    GET_INITIAL_PROFILE,
    GET_USER,
    GET_ORG,
    GET_WORK_REST_INFO,
    GET_NOTIFICATION_OPTIONS,
    GET_TIMEZONE,
    GET_REGION_SETTINGS,
    GET_REPORT_EXECUTION_TIMES,
    GET_DEFAULT_ROUTE_OPTIONS,
    GET_CONTROL_POINTS_SETTINGS,
    GET_SYNC_SYSTEMS,
    GET_PROFITABILITY_OF_BID_SETTINGS,
    GET_DRIVER_EDUCATION_RESOURCE_SETTINGS,
    GET_CHATS_SETTINGS,
    GET_REFUELING_SETTINGS,
    GET_EXPLOITATION_SETTINGS,
    GET_BID_CARS_PLANNING_SETTINGS,
);

const limitedFunctionalityFlagVal = flagVal(organizationFlags, organizationFlagsEnum.LimitedFunctionality);

const getInitial = () => (dispatch: ActionFunctionAny<Action<Object>>) => {
    return create().then(
        (res?: {
            token: string;
            currentUser: { availableFeatureModules: (keyof typeof featureModuleTypesEnum)[] };
        }) => {
            if (res?.token) {
                const reqs = [showOrg(), showFuelLimitOption(), showUser()];
                if (
                    res.currentUser.availableFeatureModules.includes(
                        featureModuleTypesEnum.RefuelingPlanning as keyof typeof featureModuleTypesEnum,
                    )
                ) {
                    reqs.push(showRefuelingSettings());
                }
                Promise.all(reqs).then(([org, isFuelLimitEnabled, userProfile, refuelingSettings]) => {
                    if (refuelingSettings) {
                        dispatch(actions.getRefuelingSettings(refuelingSettings));
                    }
                    getTimezoneInfo(org.timezoneId).then((res) => dispatch(actions.getTimezone(res)));
                    dispatch(actions.getOrg(org));
                    const isDemoPeriodExpired = org.demoPeriod?.status === organizationDemoPeriodStatusesEnum.Expired;
                    const limitedFunctionality =
                        (org.intFlags & limitedFunctionalityFlagVal) === limitedFunctionalityFlagVal;
                    dispatch(
                        actions.getInitialProfile({
                            token: res.token,
                            currentUser: {
                                ...userProfile,
                                ...res.currentUser,
                                isFuelLimitEnabled,
                                limitedFunctionality,
                                isDemoPeriodExpired,
                                refuelingSettings,
                            },
                        }),
                    );
                    if (org.location) {
                        const { coordinates } = org.location;
                        dispatch(mapActions.changeCenter([...coordinates].reverse()));
                    }
                });
            }
        },
    );
};

const getOrg = () => (dispatch: ActionFunctionAny<Action<Object>>) => {
    return showOrg().then((res) => {
        getTimezoneInfo(res.timezoneId).then((res) => dispatch(actions.getTimezone(res)));
        dispatch(actions.getOrg(res));
    });
};

const getUser = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showUser().then((res) => dispatch(actions.getUser(res)));

const getWorkRestInfo = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showWorkRestInfo().then((res) => dispatch(actions.getWorkRestInfo(res)));

const getProfitabilityOfBidSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showProfitabilityOfBidSettings().then((res) => dispatch(actions.getProfitabilityOfBidSettings(res)));

const getDriverEducationResourceSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showDriverEducationResourceSettings().then((res) => dispatch(actions.getDriverEducationResourceSettings(res)));

const getChatsSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showChatsSettings().then((res) => dispatch(actions.getChatsSettings(res)));

const getExploitationSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showExploitationSettings().then((res) => dispatch(actions.getExploitationSettings(res)));

const getReportExecutionTimes = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showReportExecutionTimes().then((res) => dispatch(actions.getReportExecutionTimes(res)));

const getDefaultRouteOptions = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showDefaultRouteOptions().then((res) => dispatch(actions.getDefaultRouteOptions(res)));

const getControlPointsSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showControlPointsSettings().then((res) => dispatch(actions.getControlPointsSettings(res)));

const getNotificationOptions = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showNotificationOptions().then((res) => dispatch(actions.getNotificationOptions(res)));

const getSyncSystems = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showSyncSystems().then((res) => dispatch(actions.getSyncSystems(res)));

const getBidCarsPlanningSettings = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
    showBidCarsPlanningSettings().then((res) => dispatch(actions.getBidCarsPlanningSettings(res)));

export default {
    getInitialProfile: actions.getInitialProfile,
    getInitial,
    getUser,
    getOrg,
    getWorkRestInfo,
    getProfitabilityOfBidSettings,
    getNotificationOptions,
    getReportExecutionTimes,
    getDefaultRouteOptions,
    getControlPointsSettings,
    getSyncSystems,
    getBidCarsPlanningSettings,
    getDriverEducationResourceSettings,
    getChatsSettings,
    getExploitationSettings,
};
